import React, { useEffect, useMemo, useState } from 'react';
import { X, Search, Phone, PhoneCall, PhoneOff, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CallRecord, answeredCount, formatDuration, formatCallDate } from '../data/calls';
type CallFilter = 'all' | 'answered' | 'missed';
interface CallsPanelProps {
  open: boolean;
  calls: CallRecord[];
  metric: 'dials' | 'answer_rate';
  dials: number;
  callAnswerRate: number;
  scopeLabel: string;
  officerName: string;
  onClose: () => void;
}
export function CallsPanel({
  open,
  calls,
  metric,
  dials,
  callAnswerRate,
  scopeLabel,
  officerName,
  onClose
}: CallsPanelProps) {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<CallFilter>('all');
  // Reset when reopened; answer-rate drilldown leads with answered context
  useEffect(() => {
    if (open) {
      setSearch('');
      setFilter('all');
    }
  }, [open, metric]);
  // Close on escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  const totalAnswered = answeredCount(calls);
  const totalMissed = calls.length - totalAnswered;
  const sampleRate = calls.length > 0 ? Math.round(totalAnswered / calls.length * 100) : 0;
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return calls.filter(c => {
      if (filter === 'answered' && !c.answered) return false;
      if (filter === 'missed' && c.answered) return false;
      if (q) {
        return c.contact.toLowerCase().includes(q) || c.phone.toLowerCase().includes(q) || c.outcome.toLowerCase().includes(q);
      }
      return true;
    });
  }, [calls, filter, search]);
  const isAnswerRate = metric === 'answer_rate';
  const accent = isAnswerRate ? 'teal' : 'indigo';
  return <AnimatePresence data-id="element-210" data-mp="jcbdm34tdpab">
      {open && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/40 z-50" onClick={onClose} aria-hidden="true" data-id="element-211" data-mp="obwhmryucntw" />
          <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'tween',
        duration: 0.25,
        ease: 'easeOut'
      }} className="fixed top-0 right-0 bottom-0 w-full md:w-[720px] bg-gray-50 z-50 shadow-2xl flex flex-col" role="dialog" aria-label={isAnswerRate ? 'Call answer rate detail' : 'Dials detail'} data-id="element-212" data-mp="124vsuhrlbvvn">
            {/* Header */}
            <div className={`${isAnswerRate ? 'bg-teal-600' : 'bg-indigo-600'} text-white px-6 py-4 flex items-center justify-between shrink-0`} data-id="element-213" data-mp="3ahhhq6zhbk4f">
              <div className="flex items-center gap-3 min-w-0" data-id="element-214" data-mp="3k82f5r3f2ilr">
                <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center shrink-0" data-id="element-215" data-mp="3f9r3rbxh401f">
                  {isAnswerRate ? <Phone className="w-5 h-5" data-id="element-216" data-mp="2rkygs4ytx2iy" /> : <PhoneCall className="w-5 h-5" data-id="element-217" data-mp="2mmoj9npmtpu6" />}
                </div>
                <div className="min-w-0" data-id="element-218" data-mp="31jxewtlyxfrp">
                  <p className={`text-xs font-semibold uppercase tracking-wider ${isAnswerRate ? 'text-teal-100' : 'text-indigo-100'}`} data-id="element-219" data-mp="31gt4hehwtnsl">
                    {officerName} · {scopeLabel}
                  </p>
                  <h2 className="text-lg font-bold leading-tight truncate" data-id="element-220" data-mp="budb4il1vegl">
                    {isAnswerRate ? 'Call Answer Rate' : 'Dials'}
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-white/15 flex items-center justify-center transition-colors" aria-label="Close panel" data-id="element-221" data-mp="2mg5sqt2gprqw">
                <X className="w-5 h-5" data-id="element-222" data-mp="2vj6smc0qimm4" />
              </button>
            </div>

            {/* Summary stats */}
            <div className="bg-white border-b border-gray-200 px-6 py-3 grid grid-cols-4 gap-4 shrink-0" data-id="element-223" data-mp="30em6h3yqsiij">
              <SummaryStat label="Total Dials" value={dials.toLocaleString()} valueClass="text-[#0a1e5e]" data-id="element-224" data-mp="35dgnq1h29z0j" />
              <SummaryStat label="Answered" value={totalAnswered.toString()} valueClass="text-green-600" data-id="element-225" data-mp="3e7lnjae6aks7" />
              <SummaryStat label="Missed" value={totalMissed.toString()} valueClass="text-red-500" data-id="element-226" data-mp="3o5hwpwnjv5no" />
              <SummaryStat label="Answer Rate" value={`${callAnswerRate}%`} valueClass={isAnswerRate ? 'text-teal-600' : 'text-[#0a1e5e]'} data-id="element-227" data-mp="22hbsc3xyyh4" />
            </div>

            {/* Context note */}
            <div className={`${isAnswerRate ? 'bg-teal-50 border-teal-200 text-teal-900' : 'bg-indigo-50 border-indigo-200 text-indigo-900'} border-b px-6 py-2.5 text-xs flex items-start gap-2 shrink-0`} data-id="element-228" data-mp="3kbruojch6xdg">
              {isAnswerRate ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" data-id="element-229" data-mp="b509khsvekwi" /> : <PhoneCall className="w-4 h-4 shrink-0 mt-0.5" data-id="element-230" data-mp="746m1pjcb845" />}
              <p data-id="element-231" data-mp="723xzb1qzp22">
                Showing <strong data-id="element-232" data-mp="13lktqi56eme3">{calls.length}</strong> recent calls (
                {sampleRate}% answered) of{' '}
                <strong data-id="element-233" data-mp="yuulmx5d85fp">{dials.toLocaleString()}</strong> total dials this
                period.{' '}
                {isAnswerRate ? 'Answered calls drive your answer rate.' : 'Every outbound call counts toward your dials.'}
              </p>
            </div>

            {/* Filters */}
            <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0" data-id="element-234" data-mp="2jcdk49ysxi5w">
              <div className="relative mb-3" data-id="element-235" data-mp="2ob6n38ggpqrt">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-236" data-mp="2o9vix7j3uh06" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by contact, number, or outcome…" className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-237" data-mp="2aethoxp7dftp" />
              </div>
              <div className="flex gap-1.5" data-id="element-238" data-mp="3miwk1lscnys">
                {([{
              id: 'all',
              label: 'All Calls'
            }, {
              id: 'answered',
              label: 'Answered'
            }, {
              id: 'missed',
              label: 'Missed'
            }] as {
              id: CallFilter;
              label: string;
            }[]).map(f => {
              const active = filter === f.id;
              return <button key={f.id} onClick={() => setFilter(f.id)} className={`px-3 py-1 text-xs font-bold rounded-full border transition-colors ${active ? `${isAnswerRate ? 'bg-teal-600 border-teal-600' : 'bg-indigo-600 border-indigo-600'} text-white` : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`} data-id="element-239" data-mp="22vaiphzkdbzh">
                      {f.label}
                    </button>;
            })}
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto px-6 py-4" data-id="element-240" data-mp="jpfw5dmu8gr">
              {filtered.length === 0 ? <div className="text-center py-12 text-gray-500 font-semibold" data-id="element-241" data-mp="5d0ly4amel7m">
                  No calls match your filters.
                </div> : <ul className="space-y-2" data-id="element-242" data-mp="38u0okplr63nz">
                  {filtered.map(c => <li key={c.id} className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3" data-id="element-243" data-mp="3dr91obs1nhid">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${c.answered ? 'bg-green-100' : 'bg-red-50'}`} data-id="element-244" data-mp="3nqrhcg0x8h4m">
                        {c.answered ? <PhoneCall className="w-4 h-4 text-green-600" data-id="element-245" data-mp="12yfy2c8mivxx" /> : <PhoneOff className="w-4 h-4 text-red-500" data-id="element-246" data-mp="2nhi852crtvjk" />}
                      </div>
                      <div className="min-w-0 flex-1" data-id="element-247" data-mp="2ilb3re00ws3q">
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-248" data-mp="2dqxg5xrlrp9m">
                          <p className="text-sm font-bold text-gray-800 leading-tight" data-id="element-249" data-mp="28tnf1kuupp5y">
                            {c.contact}
                          </p>
                          <span className="text-xs text-gray-400" data-id="element-250" data-mp="23ry568jd7hjg">
                            {c.phone}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 leading-tight mt-0.5 truncate" data-id="element-251" data-mp="1uu5q95hk2bwl">
                          {c.outcome} · {formatCallDate(c.date)} · {c.time}
                        </p>
                      </div>
                      <div className="text-right shrink-0" data-id="element-252" data-mp="3p6m0sihdwc79">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${c.answered ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`} data-id="element-253" data-mp="3k9c5wnb878c1">
                          {c.answered ? 'Answered' : 'Missed'}
                        </span>
                        <p className="text-[11px] font-semibold text-gray-500 mt-1" data-id="element-254" data-mp="fgez0qnx9ctj">
                          {formatDuration(c.durationSec)}
                        </p>
                      </div>
                    </li>)}
                </ul>}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
function SummaryStat({
  label,
  value,
  valueClass
}: {
  label: string;
  value: string;
  valueClass: string;
}) {
  return <div className="min-w-0" data-id="element-255" data-mp="3nwgh418xe76h">
      <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider" data-id="element-256" data-mp="308qiovk4t00r">
        {label}
      </p>
      <p className={`text-2xl font-extrabold leading-tight ${valueClass}`} data-id="element-257" data-mp="2w7wus26ovl7v">
        {value}
      </p>
    </div>;
}